export const POSTAGE_FORMULAS = {
  invoiceAmount: {
    labelKey: 'billingPage.PostageRechargeInvoiceAmount',
    descriptionKey: 'billingPage.PostageRechargeInvoiceFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeInvoiceFormula',
    fields: ['invoiceAmount', 'matchedAmount', 'unmatchedAmount']
  },
  difference: {
    labelKey: 'billingPage.PostageRechargeDifference',
    descriptionKey: 'billingPage.PostageRechargeDifferenceFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeDifferenceFormula',
    fields: ['matchedAmount', 'costReceived', 'difference']
  },
  currentDifference: {
    labelKey: 'billingPage.PostageRechargeCurrentDifference',
    descriptionKey: 'billingPage.PostageRechargeCurrentDifferenceFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeCurrentDifferenceFormula',
    fields: ['totalOrderCost', 'orderCostReceived', 'currentDifference']
  },
  currentChargeAmount: {
    labelKey: 'billingPage.PostageRechargeCurrentChargeAmount',
    descriptionKey: 'billingPage.PostageRechargeCurrentChargeFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeCurrentChargeFormula',
    fields: ['currentDifference', 'profitMargin', 'currentChargeAmount']
  },
  listCurrentChargeAmount: {
    labelKey: 'billingPage.PostageRechargeCurrentChargeAmount',
    descriptionKey: 'billingPage.PostageRechargeListChargeFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeListChargeFormula',
    fields: []
  },
  profitMargin: {
    labelKey: 'billingPage.PostageRechargeProfitMargin',
    descriptionKey: 'billingPage.PostageRechargeProfitMarginFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeProfitMarginFormula',
    fields: ['currentDifference', 'currentChargeAmount', 'profitMargin']
  },
  averageProfitMargin: {
    labelKey: 'billingPage.PostageRechargeAverageProfitMargin',
    descriptionKey: 'billingPage.PostageRechargeAverageMarginFormulaDesc',
    formulaKey: 'billingPage.PostageRechargeAverageMarginFormula',
    fields: ['averageProfitMargin']
  }
};

function isPresent(value) {
  return value !== null && value !== undefined && value !== '';
}

function createInvoiceValidationErrors(row) {
  if (![row.invoiceAmount, row.matchedAmount, row.unmatchedAmount].every(isPresent)) return [];
  const expectedValue = Number(row.matchedAmount) + Number(row.unmatchedAmount);
  const actualValue = Number(row.invoiceAmount);
  if (Math.abs(expectedValue - actualValue) < 0.01) return [];
  return [{
    field: 'invoiceAmount',
    code: 'INVOICE_AMOUNT_MISMATCH',
    message: 'Invoice amount does not equal matched amount plus unmatched amount.',
    expectedValue,
    actualValue
  }];
}

export function adaptPostageInvoiceRow(row = {}) {
  return {
    ...row,
    difference: row.costDifference,
    currentChargeAmount: row.difference,
    averageProfitMargin: row.averageMargin,
    validationErrors: row.validationErrors || createInvoiceValidationErrors(row)
  };
}

export function adaptPostageInvoiceRows(rows = []) {
  return (rows || []).map(adaptPostageInvoiceRow);
}

export function sortNumericRows(rows = [], sort = {}) {
  const { prop, order } = sort;
  if (!prop || !['ascending', 'descending'].includes(order)) return rows || [];

  const direction = order === 'ascending' ? 1 : -1;
  return (rows || [])
    .map((row, index) => ({
      row,
      index,
      value: toFiniteNumber(row[prop])
    }))
    .sort((left, right) => {
      const leftMissing = left.value === null;
      const rightMissing = right.value === null;
      if (leftMissing && rightMissing) return left.index - right.index;
      if (leftMissing) return 1;
      if (rightMissing) return -1;
      if (left.value === right.value) return left.index - right.index;
      return (left.value - right.value) * direction;
    })
    .map(item => item.row);
}

function toFiniteNumber(value) {
  if (!isPresent(value)) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function adaptPostageInvoiceSummary(response = {}) {
  return {
    invoiceAmount: response.totalInvoiceAmt,
    matchedAmount: response.matchedPrice,
    unmatchedAmount: response.unmarchedPrice,
    currentChargeAmount: response.totalDifference,
    averageProfitMargin: response.averageMargin,
    pendingUploads: response.pendingUploads
  };
}

function normalizePercentage(marginRate) {
  if (!isPresent(marginRate)) return null;
  const percentage = Number(marginRate);
  return Number.isFinite(percentage) ? percentage : null;
}

function adaptMatchedOrders(groups = []) {
  const rows = [];
  (groups || []).forEach(group => {
    (group.items || []).forEach(item => {
      rows.push({
        ...item,
        orderNumber: group.orderNumber,
        matchOrderNumber: item.orderNumber,
        trackingNumber: item.consignmentNum,
        invoiceAmount: item.invoicePrice,
        orderCostReceived: item.costReceived,
        orderAmountReceived: item.amountReceived,
        totalOrderCost: item.totalInvoicePrice,
        currentDifference: item.currentDifference,
        currentChargeAmount: item.difference,
        profitMargin: normalizePercentage(item.marginRate),
        validationErrors: item.validationErrors || []
      });
    });
  });
  return rows;
}

export function adaptPostageInvoiceDetail(response = {}) {
  return {
    ...response,
    difference: response.costDifference,
    currentChargeAmount: response.difference,
    averageProfitMargin: response.averageMargin,
    matchedOrders: adaptMatchedOrders(response.matchedOrders),
    unmatchedItems: response.unmatchedItems || [],
    validationErrors: response.validationErrors || createInvoiceValidationErrors(response)
  };
}
